import React, {useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {createFilterOptions, DialogContent, DialogTitle, Divider} from "@mui/material";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {Col} from "reactstrap";
import Form from "../../base/form";
import Button from "../../base/button/button";
import {MuiRFF, Yup} from '../../form';


const schema = Yup.object().shape({
    newIndex: Yup.number().nullable().required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('Order'))
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);
const filter = createFilterOptions();


const ChangeOrderDialog = ({
                               open,
                               close,
                               orderIndex,
                               length,
                               reorder,
                               message,
                           }) => {
    const [submitting, setSubmitting] = useState(false);
    const orders = useMemo(() => Array(length)
            .fill(null)
            .map((_, e) => e + 1)
            .filter(e => e !== orderIndex),
        [length, orderIndex])

    /**
     * Calls the reorder callback and awaits for its response.
     *
     * if successful, closes the dialog, otherwise
     * @param values
     * @return {Promise<{newIndex: (*|undefined)}|void>}
     */
    const onReordered = async (values) => {
        setSubmitting(true);
        const response = await reorder(Number(values.newIndex));
        setSubmitting(false);
        if (!response) {
            return;
        }
        if (response?.resultFlag) {
            close();
            return Promise.resolve();
        }
        return Promise.resolve({
            newIndex: response?.message ?? undefined,
        });
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'sm'}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Change Order
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2'}>
                    <Col xs={12} className={'mb-3 mt-4'}>
                        <p className={'text-sm font-weight-regular mt-2 mb-2'}>
                            {message ?? ""}
                        </p>
                    </Col>
                    <Col xs={12} className={'mb-4'}>
                        <p className={'text-sm font-weight-regular mt-2 mb-2'}>
                            Current Order: {orderIndex}
                        </p>
                    </Col>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={onReordered}
                            validate={validate}
                            initialValues={{newIndex: orderIndex}}
                            render={({initialValues, values, form}) => {
                                return (
                                    <>
                                        <Col className={'p-0'}>
                                            <MuiRFF.Autocomplete
                                                name={'newIndex'}
                                                placeholder={'type your order...'}
                                                label={"New Order"}
                                                required={!!required.newIndex}
                                                filterOptions={filter}
                                                options={orders.map(e => e.toString())}
                                                getOptionLabel={option => option.toString()}
                                                onBlur={e => {
                                                    if (e.target?.value?.trim()) {
                                                        let num = Number(e.target?.value?.trim());
                                                        if (num < 0 || num > length) {
                                                            num = Math.max(Math.min(num, length), 1);
                                                        }
                                                        form.change('newIndex', num);
                                                    }
                                                }}
                                            />
                                        </Col>
                                        <Col xs={'auto'} className={'d-flex flex-column mt-1'}>
                                            <Button
                                                className='py-2-5 px-5 m-auto'
                                                type={'submit'}
                                                variant={'contained'}
                                                color={'primary'}
                                                disabled={submitting || initialValues.newIndex === Number(values.newIndex)}
                                            >
                                                {
                                                    submitting
                                                        ? "Saving..."
                                                        : "Save"
                                                }
                                            </Button>
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
            </Dialog>
        </>
    )
}


export default ChangeOrderDialog;
